import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Camera,
  ChevronRight,
  Inbox,
  Loader2,
  MapPin,
  RefreshCw,
  Search,
  Star,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import EmptyState from '@/components/ui/EmptyState';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import {
  ESTADO_BADGES,
  ESTADO_LABELS,
  EXPECTATIVAS_LABELS,
  NUM_PROPIEDADES_LABELS,
  ZONA_LABELS,
  etiqueta,
  type EstadoPeticion,
  type Peticion,
} from '@/lib/peticiones';

/**
 * Peticiones de propietarios llegadas del proceso de cualificación de la web.
 *
 * Se revisan casi siempre desde el móvil: el aviso llega por Telegram con un
 * enlace directo, y quien lo abre suele estar fuera de la oficina. Por eso la
 * lista es de tarjetas en móvil y solo se convierte en tabla a partir de `md`,
 * que es el patrón del resto del CRM.
 */

function classNames(...c: (string | false | undefined)[]): string {
  return c.filter(Boolean).join(' ');
}

/** Búsqueda tolerante a mayúsculas y tildes: "Jose" encuentra a "José". */
function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

const FILTROS: { key: 'all' | EstadoPeticion; label: string }[] = [
  { key: 'nueva', label: 'Nuevas' },
  { key: 'pendiente_licencia', label: 'Pendientes' },
  { key: 'aceptada', label: 'Aceptadas' },
  { key: 'rechazada', label: 'Rechazadas' },
  { key: 'descartada', label: 'Descartadas' },
  { key: 'all', label: 'Todas' },
];

function EstadoBadge({ estado }: { estado: EstadoPeticion }) {
  return (
    <span className={classNames('badge', ESTADO_BADGES[estado] || 'bg-gray-100 text-gray-500')}>
      {ESTADO_LABELS[estado] || estado}
    </span>
  );
}

function fecha(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}

/** Resumen de una línea: lo que decide si merece abrirse. */
function resumen(p: Peticion): string {
  return [
    etiqueta(NUM_PROPIEDADES_LABELS, p.numPropiedades),
    etiqueta(ZONA_LABELS, p.zona),
    etiqueta(EXPECTATIVAS_LABELS, p.expectativas),
  ]
    .filter(Boolean)
    .join(' · ');
}

export default function PeticionesPage() {
  const [filtro, setFiltro] = useState<'all' | EstadoPeticion>('nueva');
  const [busqueda, setBusqueda] = useState('');

  // Se pide la lista entera y se filtra en memoria: son pocas decenas de
  // documentos y así los contadores de las pestañas no cambian al navegar.
  const { data, loading, error, refresh } = useApi<Peticion[]>(() => api.getPeticiones(), []);

  const peticiones = useMemo(() => data ?? [], [data]);

  const contadores = useMemo(() => {
    const acc: Record<string, number> = { all: peticiones.length };
    for (const p of peticiones) acc[p.estado] = (acc[p.estado] || 0) + 1;
    return acc;
  }, [peticiones]);

  const visibles = useMemo(() => {
    const q = normalizar(busqueda.trim());
    return peticiones
      .filter((p) => filtro === 'all' || p.estado === filtro)
      .filter(
        (p) =>
          !q ||
          normalizar(`${p.nombre || ''} ${p.email || ''} ${p.telefono || ''} ${p.direccion || ''}`).includes(q),
      )
      .sort((a, b) => {
        // Los proyectos especiales primero: son los que no queremos que se
        // pierdan entre las peticiones de una casa suelta.
        if (!!a.esProyectoEspecial !== !!b.esProyectoEspecial) return a.esProyectoEspecial ? -1 : 1;
        return (b.createdAt || '').localeCompare(a.createdAt || '');
      });
  }, [peticiones, filtro, busqueda]);

  return (
    <div>
      {/* Cabecera */}
      <div className="flex items-start justify-between mb-6 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Inbox size={24} className="text-sereno-dark shrink-0" />
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold">Peticiones</h1>
            <p className="text-sm text-sereno-dark/40 mt-0.5">
              Solicitudes de propietarios llegadas de la web, ya cualificadas.
            </p>
          </div>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="btn-ghost flex items-center gap-2 text-sm shrink-0"
          title="Recargar la lista"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          <span className="hidden sm:inline">Actualizar</span>
        </button>
      </div>

      {/* Filtros: barra deslizable en móvil, para no apilar seis pestañas */}
      <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto mb-4">
        <div className="flex gap-2 min-w-max">
          {FILTROS.map((f) => {
            const activo = filtro === f.key;
            const n = contadores[f.key] || 0;
            return (
              <button
                key={f.key}
                onClick={() => setFiltro(f.key)}
                aria-pressed={activo}
                className={classNames(
                  'h-11 px-4 rounded-xl border-2 text-sm font-medium transition-colors whitespace-nowrap',
                  activo
                    ? 'border-sereno-dark bg-sereno-dark text-white'
                    : 'border-gray-100 bg-white hover:border-slate-300',
                )}
              >
                {f.label}
                <span className={classNames('ml-1.5', activo ? 'opacity-70' : 'text-sereno-dark/40')}>
                  {n}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Buscador */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
        <input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por nombre, email, teléfono o dirección"
          className="input pl-9 h-11 w-full"
        />
      </div>

      {loading && !data && <LoadingSpinner />}

      {error && (
        <div className="card bg-red-50 border-red-200 text-red-700 text-sm">
          No se ha podido cargar la lista: {error}
        </div>
      )}

      {!loading && !error && visibles.length === 0 && (
        <EmptyState
          title={busqueda || filtro !== 'all' ? 'Nada por aquí' : 'Todavía no hay peticiones'}
          description={
            busqueda || filtro !== 'all'
              ? 'Prueba con otro filtro o cambia la búsqueda.'
              : 'Cuando alguien complete el proceso en la web, aparecerá aquí.'
          }
        />
      )}

      {visibles.length > 0 && (
        <>
          {/* Tarjetas (móvil) */}
          <div className="space-y-3 md:hidden">
            {visibles.map((p) => (
              <Link
                key={p.id}
                to={`/leads/peticiones/${p.id}`}
                className="card block active:bg-slate-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      {p.esProyectoEspecial && (
                        <Star size={14} className="text-amber-500 shrink-0" aria-label="Proyecto especial" />
                      )}
                      <span className="font-medium truncate">{p.nombre || 'Sin nombre'}</span>
                    </div>
                    <p className="text-sm text-sereno-dark/50 mt-1">{resumen(p)}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <EstadoBadge estado={p.estado} />
                    <span className="text-xs text-sereno-dark/40">{fecha(p.createdAt)}</span>
                  </div>
                </div>
                {p.direccion && (
                  <p className="text-sm text-sereno-dark/50 mt-2 flex items-start gap-1.5">
                    <MapPin size={14} className="mt-0.5 shrink-0" />
                    <span className="truncate">{p.direccion}</span>
                  </p>
                )}
                {!!p.fotos?.length && (
                  <p className="text-xs text-sereno-dark/40 mt-2 flex items-center gap-1.5">
                    <Camera size={13} /> {p.fotos.length} fotos
                  </p>
                )}
              </Link>
            ))}
          </div>

          {/* Tabla (escritorio) */}
          <div className="card overflow-x-auto hidden md:block p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-sereno-dark/50">
                  <th className="px-4 py-3 font-medium">Propietario</th>
                  <th className="px-4 py-3 font-medium">Propiedad</th>
                  <th className="px-4 py-3 font-medium">Expectativas</th>
                  <th className="px-4 py-3 font-medium">Fotos</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Recibida</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {visibles.map((p) => (
                  <tr key={p.id} className="border-b border-gray-50 last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Link to={`/leads/peticiones/${p.id}`} className="flex items-center gap-1.5 font-medium hover:underline">
                        {p.esProyectoEspecial && <Star size={14} className="text-amber-500" aria-label="Proyecto especial" />}
                        {p.nombre || 'Sin nombre'}
                      </Link>
                      <div className="text-xs text-sereno-dark/40 mt-0.5">{p.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{etiqueta(NUM_PROPIEDADES_LABELS, p.numPropiedades) || '—'}</div>
                      <div className="text-xs text-sereno-dark/40 mt-0.5">
                        {etiqueta(ZONA_LABELS, p.zona) || (p.esProyectoEspecial ? 'Sin zona (proyecto)' : '—')}
                      </div>
                    </td>
                    <td className="px-4 py-3">{etiqueta(EXPECTATIVAS_LABELS, p.expectativas) || '—'}</td>
                    <td className="px-4 py-3">{p.fotos?.length || 0}</td>
                    <td className="px-4 py-3"><EstadoBadge estado={p.estado} /></td>
                    <td className="px-4 py-3 text-sereno-dark/50">{fecha(p.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/leads/peticiones/${p.id}`}
                        className="inline-flex items-center justify-center h-9 w-9 rounded-lg hover:bg-slate-100"
                        aria-label={`Ver la petición de ${p.nombre || 'este propietario'}`}
                      >
                        <ChevronRight size={16} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
